// Locale-aware date/time formatters. Reads useLocaleStore at call time
// (non-React-component-safe — uses .getState()). For component-level
// reactivity, wrap formatDate(iso) in useMemo([iso, locale]).
//
// Replaces all hardcoded 'tr-TR' / 'en-US' Intl.* literal calls across
// frontend/src per i18n Wave-1 (audit 2026-06-02 commit ca624ba).

import { useLocaleStore, type Locale } from '../store/locale'

export type PlainDate = `${number}-${number}-${number}`

export interface PlainDateRange {
  start: PlainDate | null
  end: PlainDate | null
}

export interface CalendarMonth {
  year: number
  month: number
}

const EASTERN_TIME_ZONE = 'America/New_York'

export function intlLocale(): string {
  return useLocaleStore.getState().locale === 'tr' ? 'tr-TR' : 'en-US'
}

function resolvedLocale(locale?: Locale): Locale {
  return locale ?? useLocaleStore.getState().locale
}

function isLeapYear(year: number): boolean {
  return year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0)
}

function daysInMonth(year: number, month: number): number {
  if (month === 2) return isLeapYear(year) ? 29 : 28
  return [4, 6, 9, 11].includes(month) ? 30 : 31
}

function parsePlainDateParts(
  value: string,
): { year: number; month: number; day: number } | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value)
  if (!match) return null

  const year = Number(match[1])
  const month = Number(match[2])
  const day = Number(match[3])
  if (
    year < 1 ||
    month < 1 ||
    month > 12 ||
    day < 1 ||
    day > daysInMonth(year, month)
  ) {
    return null
  }

  return { year, month, day }
}

export function parsePlainDate(value: string): PlainDate | null {
  return parsePlainDateParts(value) ? (value as PlainDate) : null
}

export function formatPlainDate(
  value: PlainDate,
  locale?: Locale,
): string {
  const parts = parsePlainDateParts(value)
  if (!parts) return ''

  const year = String(parts.year).padStart(4, '0')
  const month = String(parts.month).padStart(2, '0')
  const day = String(parts.day).padStart(2, '0')
  return resolvedLocale(locale) === 'tr'
    ? `${day}.${month}.${year}`
    : `${month}/${day}/${year}`
}

export function formatPlainDateRange(
  range: PlainDateRange,
  locale?: Locale,
): string {
  const start = range.start ? formatPlainDate(range.start, locale) : ''
  const end = range.end ? formatPlainDate(range.end, locale) : ''
  if (!start && !end) return ''
  if (!start) return `– ${end}`
  if (!end) return `${start} –`
  return `${start} – ${end}`
}

export function serializePlainDate(value: PlainDate | null): string {
  return value && parsePlainDate(value) ? value : ''
}

export function addPlainDays(value: PlainDate, amount: number): PlainDate {
  const parsed = parsePlainDateParts(value)
  if (!parsed) return value

  let { year, month, day } = parsed
  const direction = Math.sign(amount)

  for (let remaining = Math.abs(amount); remaining > 0; remaining -= 1) {
    day += direction
    if (day > daysInMonth(year, month)) {
      day = 1
      month += 1
      if (month > 12) {
        month = 1
        year += 1
      }
    } else if (day < 1) {
      month -= 1
      if (month < 1) {
        month = 12
        year -= 1
      }
      day = daysInMonth(year, month)
    }
  }

  return `${String(year).padStart(4, '0')}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}` as PlainDate
}

export function easternToday(): PlainDate {
  const parts = new Intl.DateTimeFormat(intlLocale(), {
    timeZone: EASTERN_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(new Date())
  const part = (type: Intl.DateTimeFormatPart['type']) =>
    parts.find((item) => item.type === type)?.value ?? ''
  return `${part('year')}-${part('month')}-${part('day')}` as PlainDate
}

export function plainDateForInstant(value: string | Date): PlainDate | null {
  const instant = typeof value === 'string' ? new Date(value) : value
  if (Number.isNaN(instant.getTime())) return null

  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: EASTERN_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(instant)
  const part = (type: Intl.DateTimeFormatPart['type']) =>
    parts.find((item) => item.type === type)?.value ?? ''
  return `${part('year')}-${part('month')}-${part('day')}` as PlainDate
}

export function monthForPlainDate(value: PlainDate): CalendarMonth {
  const parsed = parsePlainDateParts(value)
  return parsed
    ? { year: parsed.year, month: parsed.month }
    : { year: 1, month: 1 }
}

export function formatInstant(
  value: string | Date,
  locale?: Locale,
): string {
  const instant = typeof value === 'string' ? new Date(value) : value
  if (Number.isNaN(instant.getTime())) return ''

  const activeLocale = resolvedLocale(locale)
  const dateParts = new Intl.DateTimeFormat('en-US', {
    timeZone: EASTERN_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(instant)
  const timeParts = new Intl.DateTimeFormat(
    activeLocale === 'en' ? 'en-US' : 'en-GB',
    activeLocale === 'en'
      ? {
          timeZone: EASTERN_TIME_ZONE,
          hour: '2-digit',
          minute: '2-digit',
          hour12: true,
        }
      : {
          timeZone: EASTERN_TIME_ZONE,
          hour: '2-digit',
          minute: '2-digit',
          hourCycle: 'h23',
        },
  ).formatToParts(instant)
  const zone =
    new Intl.DateTimeFormat('en-US', {
      timeZone: EASTERN_TIME_ZONE,
      timeZoneName: 'short',
    })
      .formatToParts(instant)
      .find((part) => part.type === 'timeZoneName')?.value ?? ''
  const readPart = (
    parts: Intl.DateTimeFormatPart[],
    type: Intl.DateTimeFormatPart['type'],
  ) => parts.find((part) => part.type === type)?.value ?? ''

  const year = readPart(dateParts, 'year')
  const month = readPart(dateParts, 'month')
  const day = readPart(dateParts, 'day')
  const hour = readPart(timeParts, 'hour')
  const minute = readPart(timeParts, 'minute')
  const dayPeriod = readPart(timeParts, 'dayPeriod')

  return activeLocale === 'tr'
    ? `${day}.${month}.${year} ${hour}:${minute} ${zone}`
    : `${month}/${day}/${year}, ${hour}:${minute} ${dayPeriod} ${zone}`
}

export function formatDate(
  iso: string | Date | null | undefined,
  options?: Intl.DateTimeFormatOptions,
): string {
  // A missing date is a dash, not a white screen. This helper is called from
  // dozens of render paths; when an API field it expected turned out to be
  // absent, `undefined.toLocaleDateString()` took an entire admin page down
  // rather than leaving one label blank. Degrading is the only proportionate
  // response to "we do not know this date".
  if (iso === null || iso === undefined) return '—'
  const date = typeof iso === 'string' ? new Date(iso) : iso
  if (Number.isNaN(date.getTime())) return '—'
  return date.toLocaleDateString(intlLocale(), options ?? { dateStyle: 'medium' })
}

export function formatTime(iso: string | Date, options?: Intl.DateTimeFormatOptions): string {
  const date = typeof iso === 'string' ? new Date(iso) : iso
  return date.toLocaleTimeString(intlLocale(), options ?? { timeStyle: 'short' })
}

export function formatDateTime(iso: string | Date, options?: Intl.DateTimeFormatOptions): string {
  const date = typeof iso === 'string' ? new Date(iso) : iso
  return date.toLocaleString(
    intlLocale(),
    options ?? { dateStyle: 'medium', timeStyle: 'short' },
  )
}

const RELATIVE_TR = {
  now: 'şimdi',
  secondsAgo: (n: number) => `${n} sn önce`,
  minutesAgo: (n: number) => `${n} dk önce`,
  hoursAgo: (n: number) => `${n} sa önce`,
  daysAgo: (n: number) => `${n} gün önce`,
  weeksAgo: (n: number) => `${n} hf önce`,
  monthsAgo: (n: number) => `${n} ay önce`,
  yearsAgo: (n: number) => `${n} yıl önce`,
}

const RELATIVE_EN = {
  now: 'now',
  secondsAgo: (n: number) => `${n}s ago`,
  minutesAgo: (n: number) => `${n}m ago`,
  hoursAgo: (n: number) => `${n}h ago`,
  daysAgo: (n: number) => `${n}d ago`,
  weeksAgo: (n: number) => `${n}w ago`,
  monthsAgo: (n: number) => `${n}mo ago`,
  yearsAgo: (n: number) => `${n}y ago`,
}

export function formatRelative(iso: string | Date): string {
  const date = typeof iso === 'string' ? new Date(iso) : iso
  const diffSec = Math.floor((Date.now() - date.getTime()) / 1000)
  const dict = useLocaleStore.getState().locale === 'tr' ? RELATIVE_TR : RELATIVE_EN

  if (diffSec < 10) return dict.now
  if (diffSec < 60) return dict.secondsAgo(diffSec)
  if (diffSec < 3600) return dict.minutesAgo(Math.floor(diffSec / 60))
  if (diffSec < 86400) return dict.hoursAgo(Math.floor(diffSec / 3600))
  if (diffSec < 604800) return dict.daysAgo(Math.floor(diffSec / 86400))
  if (diffSec < 2592000) return dict.weeksAgo(Math.floor(diffSec / 604800))
  if (diffSec < 31536000) return dict.monthsAgo(Math.floor(diffSec / 2592000))
  return dict.yearsAgo(Math.floor(diffSec / 31536000))
}

export function formatDateRange(startIso: string | Date, endIso: string | Date): string {
  const start = typeof startIso === 'string' ? new Date(startIso) : startIso
  const end = typeof endIso === 'string' ? new Date(endIso) : endIso
  const sameDay = start.toDateString() === end.toDateString()
  if (sameDay) {
    return `${formatDate(start)} ${formatTime(start)}–${formatTime(end)}`
  }
  return `${formatDate(start)} – ${formatDate(end)}`
}
